import axios, { axiosNoAuth } from "./axios";

export type OrderProduct =
  | "keeper_1y"
  | "keeper_5y"
  | "candle_1y"
  | "candle_5y"
  | "highlight";

// Starts the payment of one product for one memory page and returns the Stripe address to open.
// The purchase is activated by Stripe's confirmation on the server, not by anything the page does.
const createCheckout = async (payload: {
  product: OrderProduct;
  obituaryId: number;
  displayName?: string;
}) => {
  try {
    const response = await axios.post("/orders/checkout", payload);
    return response.data as { orderId: number; paymentUrl: string };
  } catch (error: any) {
    throw new Error(
      error?.response?.data?.message || "Plačila trenutno ni mogoče pripraviti. Poskusi znova."
    );
  }
};

// Status of one of the buyer's own orders; the server confirms with Stripe and activates if paid.
const getOrderStatus = async (orderId: string | number) => {
  const response = await axios.get(`/orders/status/${orderId}`);
  return response.data as {
    orderId: number;
    status: "pending" | "paid" | "canceled" | "refunded";
    packageType: string;
    amount: number;
    paidAt: string | null;
  };
};

// Names shown under the page's single animated candle: candle buyers and paid (1/5 year) Keepers.
// Empty when there are none, in which case the page shows no candle block at all.
const getPremiumCandleNames = async (obituaryId: number | string) => {
  const response = await axiosNoAuth.get("/orders/premium-candles", { params: { obituaryId } });
  return (response.data?.names ?? []) as string[];
};

export type AdminPaymentRow = {
  type: "order" | "trial";
  id: number;
  product: string;
  memoryPage: { name: string; slugKey: string | null };
  paidAt: string | null;
  price: number | null;
  since: string | null;
  expires: string | null;
  // "active" (in use), "" (not started / not paid yet) or "cancelled" (removed, expired, deleted)
  status: "active" | "" | "cancelled";
  removedAt: string | null;
  removable: boolean;
  buyer: string | null;
};

// Admin: all paid purchases and 10-day trial Keepers in one list.
const adminPayments = async () => {
  const response = await axios.get("/admin/payments");
  return (response.data?.rows ?? []) as AdminPaymentRow[];
};
// Takes the product off (candle, message, highlight, Keeper); the payment record always stays.
const adminRemovePayment = async (type: "order" | "trial", id: number) => {
  const response = await axios.delete(`/admin/payments/${type}/${id}`);
  return response.data;
};

export type MyOrder = {
  id: number;
  title: string;
  obituaryName: string | null;
  obituarySlug: string | null;
  displayName: string | null;
  amount: number;
  paidAt: string | null;
  validUntil: string | null;
};

// "Moja naročila": what the logged-in user has paid for.
const getMyOrders = async () => {
  const response = await axios.get("/orders/mine");
  return (response.data?.orders ?? []) as MyOrder[];
};

export type KeeperStatusRow = {
  obituaryId: number;
  name: string;
  sirName: string;
  slugKey: string | null;
  who: string;
  how: "keeper" | "candle" | "gratis";
  paidAt: string;
  years: number | null;
  days?: number;
  validUntil: string;
  pageValidUntil: string | null;
  pageExpired: boolean;
};

// "Potek statusa Skrbnika": who paid for Keeper time / premium candles on the pages the user is Keeper of.
const getKeeperStatus = async () => {
  const response = await axios.get("/orders/keeper-status");
  return (response.data?.rows ?? []) as KeeperStatusRow[];
};

const orderService = { getMyOrders, getKeeperStatus, adminPayments, adminRemovePayment, createCheckout, getOrderStatus, getPremiumCandleNames };

export default orderService;
